/**
 * Tuning systems for pitch generation, shared by the training tools (and,
 * later, the score player).
 *
 * Just intonation here is 5-limit, relative to a reference note: the
 * classic ratios singers tune to in a cappella work. Intervals beyond an
 * octave reuse the ratio of the interval inside the octave, times two.
 *
 * Choices for the ambiguous intervals (change here if the ear says otherwise):
 *  - tritone: 45/32, the augmented fourth (the diminished fifth, 64/45, is
 *    its inversion; the difference is 20 cents).
 *  - minor seventh: 9/5, the 5-limit "just" minor seventh, 18 cents below
 *    equal temperament. Barbershop-style dominant sevenths use the harmonic
 *    seventh, 7/4, 31 cents below; swap the ratio if that is wanted.
 */

export const TUNINGS = {
  equal: 'equal',
  just: 'just'
};

// Semitones above the reference -> frequency ratio to the reference
export const JUST_RATIOS = [
  1,        // unison
  16 / 15,  // minor 2nd
  9 / 8,    // major 2nd
  6 / 5,    // minor 3rd
  5 / 4,    // major 3rd
  4 / 3,    // perfect 4th
  45 / 32,  // tritone (augmented 4th)
  3 / 2,    // perfect 5th
  8 / 5,    // minor 6th
  5 / 3,    // major 6th
  9 / 5,    // minor 7th
  15 / 8    // major 7th
];

const A4_HZ = 440;
const A4_MIDI = 69;

export const midiToHz = (midi) => A4_HZ * Math.pow(2, (midi - A4_MIDI) / 12);
export const hzToMidi = (hz) => A4_MIDI + 12 * Math.log2(hz / A4_HZ);

// Ratio of an interval (in semitones, any sign, any size) under a tuning
export function intervalRatio(semitones, tuning = TUNINGS.equal) {
  if (tuning !== TUNINGS.just) return Math.pow(2, semitones / 12);
  const sign = semitones < 0 ? -1 : 1;
  const size = Math.abs(semitones);
  const octaves = Math.floor(size / 12);
  const ratio = JUST_RATIOS[size % 12] * Math.pow(2, octaves);
  return sign < 0 ? 1 / ratio : ratio;
}

// How far (in cents) the tuned interval sits from its equal-tempered size.
// Zero for equal temperament; e.g. a just major third is -13.7.
export function intervalCentsOffset(semitones, tuning = TUNINGS.equal) {
  if (tuning !== TUNINGS.just) return 0;
  return 1200 * Math.log2(intervalRatio(semitones, TUNINGS.just)) - 100 * semitones;
}

// Frequency of a note that sits `semitones` above `referenceHz`
export function intervalHz(referenceHz, semitones, tuning = TUNINGS.equal) {
  return referenceHz * intervalRatio(semitones, tuning);
}
